import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Search, FileText, CreditCard, AlertTriangle, Clock, Trash2, Pencil } from 'lucide-react';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
const methodLabels = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', pos: 'POS', online: 'Online' };

const BOPayments = () => {
  const navigate = useNavigate();
  const { role } = useBOAuth();
  const isReadOnly = role === 'accountant';
  const [payments, setPayments] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMethod, setFilterMethod] = useState('');
  const [confirm, setConfirm] = useState({ open: false, payment: null });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openEdit = (p) => {
    setEditRow(p);
    setEditForm({
      payment_date: p.payment_date || '',
      amount_paid: p.amount_paid || 0,
      payment_method: p.payment_method || 'bank_transfer',
      bank_reference: p.bank_reference || '',
      notes: p.notes || '',
    });
  };
  const submitEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      await boAPI.updatePayment(editRow.id, {
        ...editForm,
        amount_paid: parseFloat(editForm.amount_paid) || 0,
      });
      toast.success('Payment updated');
      setEditRow(null);
      fetchPayments();
      fetchReminders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update payment'); }
    finally { setSaving(false); }
  };

  useEffect(() => { fetchPayments(); fetchReminders(); }, []);
  useEffect(() => { fetchPayments(); }, [filterMethod]);

  const fetchPayments = async () => {
    try {
      const params = {};
      if (filterMethod) params.method = filterMethod;
      const res = await boAPI.getPayments(params);
      setPayments(res.data.payments);
    } catch (e) { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  };
  const fetchReminders = async () => {
    try { const res = await boAPI.getPaymentReminders(); setReminders(res.data); }
    catch (e) { console.error('fetchReminders failed:', e); }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  const totalCollected = payments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-payments-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Payments</h1>
          <p className="text-neutral-500 mt-1">{payments.length} payment{payments.length !== 1 ? 's' : ''} recorded</p>
        </div>

        {/* Reminders */}
        {reminders && (reminders.overdue_count > 0 || reminders.due_today_count > 0 || reminders.due_week_count > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {reminders.overdue_count > 0 && (
              <Card className="border-red-200 bg-red-50 shadow-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div><p className="font-semibold text-red-800">{reminders.overdue_count} Overdue</p><p className="text-xs text-red-600">Payments past due date</p></div>
                </CardContent>
              </Card>
            )}
            {reminders.due_today_count > 0 && (
              <Card className="border-amber-200 bg-amber-50 shadow-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div><p className="font-semibold text-amber-800">{reminders.due_today_count} Due Today</p><p className="text-xs text-amber-600">Payments expected today</p></div>
                </CardContent>
              </Card>
            )}
            {reminders.due_week_count > 0 && (
              <Card className="border-blue-200 bg-blue-50 shadow-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div><p className="font-semibold text-blue-800">{reminders.due_week_count} Due This Week</p><p className="text-xs text-blue-600">Upcoming payments</p></div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Stats */}
        <Card className="border-neutral-border shadow-card">
          <CardContent className="p-4">
            <p className="text-xs text-neutral-500">Total Collected</p>
            <p className="text-2xl font-bold text-green-700">{formatNaira(totalCollected)}</p>
          </CardContent>
        </Card>

        {/* Filter */}
        <div className="flex gap-3">
          <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-method">
            <option value="">All Methods</option>
            <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="pos">POS</option><option value="online">Online</option>
          </select>
        </div>

        {/* Overdue Clients Detail */}
        {reminders && reminders.overdue.length > 0 && (
          <Card className="border-red-200 shadow-card">
            <CardHeader><CardTitle className="text-lg text-red-800">Overdue Payments</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {reminders.overdue.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-xl border border-red-100 bg-red-50/50">
                    <div><p className="font-medium">{c.full_name}</p><p className="text-xs text-neutral-500">{c.phone} | Due: {c.next_payment_due}</p></div>
                    <p className="font-semibold text-red-700">{formatNaira((c.total_price || 0) - (c.amount_paid || 0))} owed</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Client</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Method</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Receipt</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-b hover:bg-neutral-50 transition-colors" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-neutral-600">{p.payment_date}</td>
                    <td className="px-4 py-3 font-medium">{p.client_info?.full_name || '--'}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatNaira(p.amount_paid)}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-neutral-600">{methodLabels[p.payment_method] || p.payment_method}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-neutral-500 text-xs">{p.receipt_number}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/backoffice/receipt/${p.id}`)} className="p-2 rounded-lg hover:bg-neutral-100 text-primary-700" title="View Receipt" data-testid={`receipt-${p.id}`}><FileText className="h-4 w-4" /></button>
                        {!isReadOnly && <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700" title="Edit payment" data-testid={`edit-payment-${p.id}`}><Pencil className="h-4 w-4" /></button>}
                        {!isReadOnly && <button onClick={() => setConfirm({ open: true, payment: p })} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete payment" data-testid={`delete-payment-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No payments recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && setConfirm({ open: false, payment: null })}
        title="Delete this payment?"
        description={confirm.payment ? `Receipt ${confirm.payment.receipt_number} for ${formatNaira(confirm.payment.amount_paid)} will be removed and the related sale's totals updated. This cannot be undone.` : ''}
        confirmLabel="Delete payment"
        onConfirm={async () => {
          try {
            await boAPI.deletePayment(confirm.payment.id);
            toast.success('Payment deleted');
            setConfirm({ open: false, payment: null });
            fetchPayments();
            fetchReminders();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete payment');
          }
        }}
      />

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader><DialogTitle>Edit Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={editForm.payment_date || ''} onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })} data-testid="edit-payment-date" />
            </div>
            <div>
              <Label>Amount (₦)</Label>
              <Input type="number" step="0.01" value={editForm.amount_paid ?? ''} onChange={(e) => setEditForm({ ...editForm, amount_paid: e.target.value })} data-testid="edit-payment-amount" />
            </div>
            <div>
              <Label>Method</Label>
              <select value={editForm.payment_method} onChange={(e) => setEditForm({ ...editForm, payment_method: e.target.value })} className="w-full border rounded-md h-10 px-3 text-sm" data-testid="edit-payment-method">
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="pos">POS</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div>
              <Label>Bank Reference</Label>
              <Input value={editForm.bank_reference || ''} onChange={(e) => setEditForm({ ...editForm, bank_reference: e.target.value })} data-testid="edit-payment-ref" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-payment-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} data-testid="save-payment-btn">{saving ? 'Saving...' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BOLayout>
  );
};

export default BOPayments;
